import { Link } from 'react-router-dom';
import { Compass, ArrowRight, X } from 'lucide-react';
import GuidedTour, { useTour } from './GuidedTour';
import { usePathScope } from '../context/PathScopeContext';
import { useAuth } from '../context/AuthContext';
import { useState, useEffect } from 'react';

/**
 * The walk-through offer, and the first three places worth going.
 *
 * This used to be a full "You're all set, welcome, {name}" panel above the dashboard
 * greeting, which meant two welcomes stacked on each other and every module pushed a
 * screen down. The path itself now lives in the Your Path bar; this is a single thin
 * line, dismissed for good once taken.
 */
export const PathTourInvite = () => {
  const { user } = useAuth();
  const { loading, hasScope, scope } = usePathScope();
  const { showTour, startTour, closeTour } = useTour('student');
  const [open, setOpen] = useState(false);

  const key = user?.id ? `fp_path_confirmed_${user.id}` : null;

  useEffect(() => {
    if (!loading && hasScope && key && !localStorage.getItem(key)) setOpen(true);
  }, [loading, hasScope, key]);

  const dismiss = () => {
    if (key) localStorage.setItem(key, '1');
    setOpen(false);
  };

  const tour = <GuidedTour isOpen={showTour} onClose={closeTour} userRole="student" />;
  if (!open || !hasScope) return tour;

  const starts = (scope?.modules || []).slice(0, 3);

  return (
    <>
      {tour}
      <div className="relative mb-4 flex flex-wrap items-center gap-x-3 gap-y-2 rounded-xl border
                      border-cyan-500/25 bg-cyan-400/[0.06] pl-4 pr-9 py-2.5"
        data-testid="path-tour-invite">
        <button onClick={dismiss} data-testid="path-tour-dismiss" aria-label="Dismiss"
          className="absolute top-2 right-2 text-slate-300 hover:text-white transition-colors duration-200">
          <X className="w-3.5 h-3.5" />
        </button>

        <button onClick={() => { startTour(); dismiss(); }} data-testid="path-welcome-tour"
          className="inline-flex items-center gap-1.5 rounded-full bg-cyan-500 hover:bg-cyan-400
                     px-3.5 py-1.5 text-xs font-semibold text-slate-900 transition-colors duration-200">
          <Compass className="w-3.5 h-3.5" /> Show me around
        </button>

        <span className="text-xs text-slate-300/85">First stops:</span>
        {starts.map((m) => (
          <Link key={m.key} to={m.path} data-testid={`path-start-${m.key}`}
            className="inline-flex items-center gap-1 text-xs text-slate-200 hover:text-cyan-200
                       border border-slate-700 hover:border-cyan-400/50 rounded-full px-2.5 py-1
                       transition-colors duration-200">
            {m.label} <ArrowRight className="w-3 h-3" />
          </Link>
        ))}
      </div>
    </>
  );
};

export default PathTourInvite;
